import React, { useState } from "react";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const user = await login(email.trim(), password, remember);
      toast.success(`Welcome back, ${user.name}${remember ? " · session will last 30 days" : " · 12h session"}`);
      nav(user.role === "admin" ? "/admin" : "/agent");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      {/* Left ­— brand pane */}
      <div className="relative hidden lg:flex flex-col justify-between p-10 border-r border-white/10 bg-[#09090B] bg-dots">
        <div className="flex items-center gap-3">
          <div className="w-2.5 h-2.5 bg-[#FF3B30] pulse-dot" />
          <span className="font-display font-black tracking-tighter text-2xl">THREADOPS</span>
          <span className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest">/crm</span>
        </div>
        <div className="max-w-md">
          <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-[#FF3B30] mb-4">
            ▍ Command Center
          </div>
          <h1 className="font-display font-black text-5xl xl:text-6xl tracking-tighter leading-[0.95] mb-6">
            Orchestrate<br />distributed agents<br />across the social web.
          </h1>
          <p className="text-zinc-400 text-[15px] leading-relaxed max-w-sm">
            Ingest keyword alerts. Score commercial intent. Dispatch randomized
            multi-agent campaigns without the algorithmic footprint.
          </p>
        </div>
        <div className="grid grid-cols-3 gap-6 max-w-md">
          {[
            { k: "01", v: "Syften ingest" },
            { k: "02", v: "AI orchestrator" },
            { k: "03", v: "Vision verify" },
          ].map((x) => (
            <div key={x.k} className="border-t border-white/10 pt-3">
              <div className="font-mono text-[10px] text-zinc-500">{x.k}</div>
              <div className="text-sm text-zinc-200 mt-1">{x.v}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Right — form pane */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-[#050505]">
        <form onSubmit={submit} className="w-full max-w-sm">
          <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-zinc-500 mb-3">
            /// Sign in
          </div>
          <h2 className="font-display font-black text-3xl tracking-tighter mb-8">Access console.</h2>

          <label className="block text-[11px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Email</label>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="login-email-input"
            className="w-full bg-[#09090B] border border-white/10 focus:border-white/40 focus:outline-none px-3 py-2.5 mb-4 font-mono text-sm text-zinc-100"
            placeholder="you@example.com"
          />

          <label className="block text-[11px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Password</label>
          <input
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="login-password-input"
            className="w-full bg-[#09090B] border border-white/10 focus:border-white/40 focus:outline-none px-3 py-2.5 mb-4 font-mono text-sm text-zinc-100"
            placeholder="••••••••"
          />

          <label className="flex items-center gap-2 mb-6 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              data-testid="login-remember-checkbox"
              className="w-4 h-4 accent-[var(--accent)]"
            />
            <span className="text-[12px] text-zinc-300">Keep me signed in for 30 days</span>
            <span className="ml-auto text-[10px] font-mono text-zinc-600">{remember ? "30d" : "12h"}</span>
          </label>

          <button
            type="submit"
            disabled={busy}
            data-testid="login-submit-btn"
            className="w-full bg-[#FF3B30] hover:bg-[#FF5A50] disabled:opacity-50 text-white font-medium tracking-wide py-2.5 flex items-center justify-center gap-2 transition-colors"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            {busy ? "AUTHENTICATING…" : "SIGN IN →"}
          </button>

          <div className="mt-8 pt-6 border-t border-white/5 text-[11px] font-mono text-zinc-600 leading-relaxed">
            Restricted access. All sessions are logged.<br />
            Contact your administrator to provision an agent account.
          </div>
        </form>
      </div>
    </div>
  );
}
